import { useContext, useEffect, useMemo, useState } from "react";
import { ImageContext } from "../context/imageContext";
import { primeMediaUrlsFromEntities } from "../functions/apiFunctions/mediaUrls";
import { Slide, SlideTypes, WPSlide, globalSlideData, globalTimespanObject } from "../types/Slides";
import { PostSlidePreprocessed } from "../types/transformedType";
import { transformImageSlide } from "./slideTransforms/transformImageSlide";
import { transformPostBlockSlide } from "./slideTransforms/transformPostBlockSlide";
import { transformTextSlide } from "./slideTransforms/transformTextSlide";

export function getGlobalSlideData(slide: WPSlide): globalSlideData {
  let timespanObject: globalTimespanObject = {
    hasTimespan: false
  };
  if (slide.acf.hasTimespan) {
    timespanObject = {
      hasTimespan: true,
      timespan: slide.acf.timespan
    };
  }
  return {
    menuOrder: slide.menu_order as number,
    fromDate: slide.acf.fromDate,
    toDate: slide.acf.toDate,
    ...timespanObject
  };
}

export function useProcessWordpressSlides(posts: PostSlidePreprocessed[], wpSlides: WPSlide[]) {
  const [slides, setSlides] = useState<Slide[]>([]);

  const { getImageUrl: getImages } = useContext(ImageContext);

  async function updateSlides() {
    primeMediaUrlsFromEntities(wpSlides);
    const processedSlides = await Promise.all(
      wpSlides.map<Promise<Slide[]>>(async (slide) => {
        const acfSlide = slide.acf;
        switch (acfSlide.type) {
          case SlideTypes.IMAGE:
            return await transformImageSlide(slide, getImages);
          case SlideTypes.TEXT_SLIDE:
            return await transformTextSlide(slide, getImages);
          case SlideTypes.POSTBLOCK:
            return transformPostBlockSlide(posts, slide);
          default:
            return [];
        }
      })
    );
    setSlides(processedSlides.flat());
  }

  useEffect(() => {
    updateSlides();
  }, [wpSlides, posts]);

  const filteredSlides = useMemo(() => {
    return slides.filter((slide) => {
      const imageSlideHadNoImageUrl = slide.type === SlideTypes.IMAGE && slide.imageUrl.length === 0;
      const postBlockSlideHadNoSlides = slide.type === SlideTypes.POSTBLOCK && slide.slides.length === 0;
      return !(imageSlideHadNoImageUrl || postBlockSlideHadNoSlides);
    });
  }, [slides]);

  return { slides: filteredSlides };
}
